import React from 'react';
import { Row, Col } from 'reactstrap';
import { StatusBadge } from '../../../../../components/Common';
import profileBg from '../../../../../assets/images/profileBg.png';
import ProfileHeaderActions from './ProfileHeaderActions';
import ProfileTabs from '../ProfileTabs';

/**
 * Avatar individual component
 */
const ProfileAvatar = ({ photoUrl, name }) => (
    <div
        style={{
            width: '110px',
            height: '110px',
            borderRadius: '50%',
            border: '2px solid var(--color-brand-primary)',
            backgroundColor: 'var(--color-bg-surface)',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(72,39,18,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}
    >
        {photoUrl ? (
            <img
                src={photoUrl}
                alt={name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
        ) : (
            <i className="mdi mdi-account" style={{ fontSize: '50px', color: 'var(--color-brand-primary)', opacity: 0.8 }}></i>
        )}
    </div>
);

/**
 * Basic info/details of the profile
 */
const ProfileBasicInfo = ({ fullName, memberNumber, status }) => (
    <div className="d-flex flex-column h-100 justify-content-center">
        <div className="mb-1">
            <h2 className="fw-bold mb-0" style={{ letterSpacing: '-0.5px', color: 'var(--color-brand-primary)', fontFamily: 'Outfit, sans-serif' }}>{fullName}</h2>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
            <span
                className="badge rounded-pill px-3 py-1 font-size-12 fw-bold"
                style={{
                    backgroundColor: 'rgba(72, 39, 18, 0.08)',
                    color: 'var(--color-brand-primary)',
                    border: '1px solid rgba(72, 39, 18, 0.15)',
                    fontFamily: 'Outfit, sans-serif'
                }}
            >
                ID: {memberNumber}
            </span>
            <StatusBadge status={status} />
        </div>
    </div>
);

/**
 * Main ProfileHeader component
 * Updated: Increased height to 320px and added translucid tabs inside
 */
const ProfileHeader = ({ member, onEdit, onSave, onCancel, isEditing, onPhotoChange, onNewSale, onPayDebt, onActivateMember, onDeactivateMember, onDelete, totalDebt, activeTab, toggleTab, tabs, canCreateSale, canPayDebt, canActivateMember, canDeactivateMember, canDeleteMember }) => {
    const {
        fullName = "Nome do Cliente",
        memberNumber = "----",
        status = "Ativo",
        photoUrl = null,
        activityNames = [],
        xpTotal = 0
    } = member || {};

    return (
        <div 
            className="profile-header-container mb-4 position-relative overflow-hidden"
            style={{
                borderRadius: '24px',
                border: '2px solid var(--color-brand-primary)',
                boxShadow: 'var(--shadow-premium-soft)'
            }}
        >
            {/* Banner Background */}
            <div
                style={{
                    minHeight: '320px',
                    width: '100%',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <img
                    src={profileBg}
                    alt=""
                    aria-hidden="true"
                    fetchpriority="high"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center',
                        zIndex: 0
                    }}
                />
                {/* Atividades e XP - Canto Superior Direito */}
                <div style={{ position: 'absolute', top: '24px', right: '24px', zIndex: 3 }} className="d-none d-md-flex align-items-center gap-3">
                    {/* XP Indicator */}
                    <div className="d-flex flex-column align-items-end text-end">
                        <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1.5px', color: 'var(--color-brand-secondary)', fontWeight: 'bold' }}>XP</span>
                        <span style={{ fontSize: '22px', fontWeight: '900', color: 'var(--color-brand-accent)', lineHeight: '1' }}>{xpTotal}</span>
                    </div>

                    <div style={{ height: '35px', width: '1px', backgroundColor: 'rgba(72, 39, 18, 0.15)' }}></div>

                    {/* Activities List */}
                    <div className="d-flex flex-column align-items-end text-end">
                        <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1.5px', color: 'var(--color-brand-secondary)', fontWeight: 'bold' }}>Nível</span>
                        <div className="d-flex flex-wrap gap-1 justify-content-end mt-1">
                            {activityNames && activityNames.length > 0 ? (
                                activityNames.map((name, idx) => (
                                    <span
                                        key={idx}
                                        className="badge px-3 py-1 rounded-3"
                                        style={{
                                            background: 'linear-gradient(135deg, var(--color-brand-secondary), var(--color-brand-primary))',
                                            color: '#fff',
                                            border: '1px solid rgba(72, 39, 18, 0.15)',
                                            fontWeight: '700',
                                            boxShadow: '0 4px 12px rgba(72, 39, 18, 0.1)',
                                            fontSize: '11px',
                                            fontFamily: 'Outfit, sans-serif'
                                        }}
                                    >
                                        {name}
                                    </span>
                                ))
                            ) : (
                                <span className="text-muted mt-1" style={{ fontSize: '12px', fontStyle: 'italic', fontWeight: '600' }}>Nenhuma</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Top/Main Content Area */}
                <div className="container-fluid px-4 pt-5 pb-4 position-relative flex-grow-1 d-flex align-items-center" style={{ zIndex: 2 }}>
                    <Row className="align-items-center g-4 w-100">
                        {/* Avatar Area */}
                        <Col xs="auto">
                            <div className="position-relative">
                                <ProfileAvatar photoUrl={photoUrl} name={fullName} />
                                {isEditing && (
                                    <div className="position-absolute" style={{ bottom: '-5px', right: '-5px', zIndex: 10 }}>
                                        <label className="btn btn-primary btn-sm rounded-circle d-flex align-items-center justify-content-center p-0 m-0 shadow-lg cursor-pointer" style={{ width: '36px', height: '36px', border: '2px solid white' }}>
                                            <i className="mdi mdi-camera font-size-18"></i>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="d-none"
                                                onChange={(e) => {
                                                    const file = e.target.files[0];
                                                    if (file) {
                                                        const reader = new FileReader();
                                                        reader.onloadend = () => {
                                                            if (onPhotoChange) onPhotoChange(reader.result);
                                                        };
                                                        reader.readAsDataURL(file);
                                                    }
                                                }}
                                            />
                                        </label>
                                    </div>
                                )}
                            </div>
                        </Col>

                        {/* Info Area */}
                        <Col>
                            <ProfileBasicInfo
                                fullName={fullName}
                                memberNumber={memberNumber}
                                status={status}
                            />
                            {canPayDebt && totalDebt > 0 && (
                                <div 
                                    className="mt-3 d-inline-flex align-items-center p-3 animate__animated animate__pulse"
                                    style={{
                                        backgroundColor: '#fef8f3',
                                        border: '1.5px solid var(--color-brand-accent)',
                                        borderRadius: '12px',
                                        boxShadow: '0 4px 12px rgba(226, 102, 28, 0.05)',
                                        fontFamily: 'Outfit, sans-serif'
                                    }}
                                >
                                    <div className="me-3">
                                        <span className="d-block fw-bold font-size-13 mb-0" style={{ color: 'var(--color-brand-accent)' }}>
                                            <i className="mdi mdi-alert-circle font-size-16 me-1"></i> Cliente com saldo devedor
                                        </span>
                                        <span className="d-block font-size-16 fw-900 mt-1" style={{ color: 'var(--color-brand-primary)' }}>
                                            R$ {totalDebt.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                    <button
                                        className="btn btn-sm flex-shrink-0 fw-bold px-3 py-2 border-0"
                                        onClick={onPayDebt}
                                        title="Quitar Saldo Devedor"
                                        style={{
                                            backgroundColor: 'var(--color-brand-accent)',
                                            color: '#ffffff',
                                            borderRadius: '10px',
                                            fontSize: '12px',
                                            fontFamily: 'Outfit, sans-serif',
                                            fontWeight: '800',
                                            boxShadow: '0 4px 10px rgba(226, 102, 28, 0.25)',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <i className="mdi mdi-currency-usd font-size-16 me-1"></i> Quitar
                                    </button>
                                </div>
                            )}
                        </Col>

                        {/* Action Area */}
                        <Col xs="12" md="auto" className="ms-auto align-self-center">
                            <ProfileHeaderActions
                                onNewSale={onNewSale}
                                onActivateMember={onActivateMember}
                                onDeactivateMember={onDeactivateMember}
                                onDelete={onDelete}
                                onEdit={onEdit}
                                onSave={onSave}
                                onCancel={onCancel}
                                isEditing={isEditing}
                                canCreateSale={canCreateSale}
                                canActivateMember={canActivateMember}
                                canDeactivateMember={canDeactivateMember}
                                canDeleteMember={canDeleteMember}
                            />
                        </Col>
                    </Row>
                </div>

                {/* Tabs Area (At the bottom of the image) */}
                <div className="container-fluid px-4 mt-auto position-relative d-flex align-items-end justify-content-between" style={{ zIndex: 3 }}>
                    <ProfileTabs activeTab={activeTab} toggleTab={toggleTab} tabs={tabs} />

                    {/* Membership Info (Bottom Right) */}
                    <div className="pb-3 text-end d-none d-md-block">
                        <span
                            className="px-3 py-2 rounded-pill"
                            style={{
                                backgroundColor: 'var(--color-bg-surface)',
                                border: '2px solid var(--color-brand-primary)',
                                color: 'var(--color-brand-primary)',
                                fontSize: '12px',
                                fontWeight: '700',
                                fontFamily: 'Outfit, sans-serif',
                                boxShadow: 'var(--shadow-premium-soft)'
                            }}
                        >
                            <i className="mdi mdi-calendar-check-outline me-2" style={{ color: 'var(--color-brand-accent)' }}></i>
                            Membro desde Jan 2024
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfileHeader;
